<template>
  <div>
    <b-row>
      <b-col xs="12" sm="12" md="12" lg="12" xl="12">
        <div class="d-flex justify-content-between align-items-center">
          <h5 class="">Internal</h5>
          <!-- download button -->
          <div>
            <b-button
              size="sm"
              variant="outline-success"
              @click="downloadExcel('Internal')"
              class="ml-2 mb-2"
            >
              Download
            </b-button>
          </div>
        </div>
        <b-table
          thead-class="text-center bg-light-grey"
          head-variant="light"
          bordered
          small
          show-empty
          outlined
          stacked="md"
          :fields="displayFieldsInternal"
          :items="internalCoincidences"
          id="internalCoincidencesTable"
          :busy="isLoadingCoincidences"
          :per-page="internalPerPage"
          :current-page="internalCurrentPage"
          class="mb-2 compact-table table"
        >
          <template #empty>
            <div class="text-center">
              <h6>{{ $t("thereAreNoRecordsToShow") }}</h6>
            </div>
          </template>
          <template #table-busy>
            <div class="text-center text-primary my-2">
              <b-spinner variant="primary" class="align-middle"></b-spinner>
              <p>Loading</p>
            </div>
          </template>
          <template v-slot:cell(price)="{ item }">
            {{ item.price | currency }}
          </template>
        </b-table>

        <!-- Internal table pagination -->
        <div
          class="d-flex justify-content-between align-items-center mb-3 compact-controls"
        >
          <b-pagination
            v-model="internalCurrentPage"
            :total-rows="internalRows"
            :per-page="internalPerPage"
            aria-controls="internalCoincidencesTable"
            align="center"
            size="sm"
            class="mb-0"
          ></b-pagination>

          <b-form-select
            v-model="internalPerPage"
            :options="perPageOptions"
            size="sm"
            style="width: 100px"
            class="ml-2"
          ></b-form-select>
        </div>
      </b-col>
    </b-row>

    <b-row>
      <b-col xs="12" sm="12" md="12" lg="12" xl="12">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h5>External</h5>
          <div>
            <b-button
              size="sm"
              variant="outline-secondary"
              @click="toggleVinColumn"
              class="ml-2"
            >
              <span v-if="showVinColumn"
                ><b-icon icon="eye-slash"></b-icon> Hide VIN</span
              >
              <span v-else><b-icon icon="eye"></b-icon> Show VIN</span>
            </b-button>
            <b-button
              size="sm"
              variant="outline-success"
              @click="downloadExcel('External')"
              class="ml-2"
            >
              Download
            </b-button>
          </div>
        </div>
        <div>
          <b-table
            thead-class="text-center bg-light-grey"
            head-variant="light"
            bordered
            small
            show-empty
            outlined
            stacked="md"
            :fields="displayFields"
            :items="externalCoincidences"
            id="externalCoincidencesTable"
            :busy="isLoadingCoincidences"
            :per-page="externalPerPage"
            :current-page="externalCurrentPage"
            class="compact-table table"
            responsive
          >
            <template #empty>
              <div class="text-center">
                <h6>{{ $t("thereAreNoRecordsToShow") }}</h6>
              </div>
            </template>
            <template #table-busy>
              <div class="text-center text-primary my-2">
                <b-spinner variant="primary" class="align-middle"></b-spinner>
                <p>Loading</p>
              </div>
            </template>

            <template v-slot:cell(price)="{ item }">
              {{ item.price | currency }}
            </template>
            <template v-slot:cell(vin)="{ item }">
              <span class="">{{ item.vin || "-" }}</span>
            </template>
          </b-table>
        </div>

        <!-- External table pagination -->
        <div
          class="d-flex justify-content-between align-items-center mt-1 compact-controls"
        >
          <b-pagination
            v-model="externalCurrentPage"
            :total-rows="externalRows"
            :per-page="externalPerPage"
            aria-controls="externalCoincidencesTable"
            align="center"
            size="sm"
            class="mb-0"
          ></b-pagination>

          <b-form-select
            v-model="externalPerPage"
            :options="perPageOptions"
            size="sm"
            style="width: 100px"
            class="ml-2"
          ></b-form-select>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "VehiclesCoincidencesTable",
  props: {
    vehicleId: {
      type: [Number, String],
      default: null,
    },
  },
  computed: {
    ...mapState({
      coincidences: (state) =>
        state.adminStore.vehiclesProviderPrice.coincidences.data || {},
      isLoadingCoincidences: (state) =>
        state.adminStore.vehiclesProviderPrice.coincidences.status ===
        "fetching",
    }),
    internalCoincidences() {
      return this.coincidences.internal || [];
    },
    externalCoincidences() {
      return this.coincidences.external || [];
    },
    // Dynamically add VIN column to internal fields when showVinColumn is true
    displayFieldsInternal() {
      if (this.showVinColumn) {
        // Create a copy of the fields array and add VIN field at index 3 (after Model)
        const fieldsWithVin = [...this.fieldsInternal];
        fieldsWithVin.splice(3, 0, this.vinField);
        return fieldsWithVin;
      }
      return this.fieldsInternal;
    },

    // Dynamically add VIN column to external fields when showVinColumn is true
    displayFields() {
      if (this.showVinColumn) {
        // Create a copy of the fields array and add VIN field at index 4 (after Model)
        const fieldsWithVin = [...this.fields];
        fieldsWithVin.splice(4, 0, this.vinField);
        return fieldsWithVin;
      }
      return this.fields;
    },

    // Internal coincidences table pagination
    internalRows() {
      return this.internalCoincidences?.length || 0;
    },

    // External coincidences table pagination
    externalRows() {
      return this.externalCoincidences?.length || 0;
    },
  },
  data() {
    return {
      // Visibility states
      showVinColumn: false,
      // Pagination
      internalPerPage: 5,
      internalCurrentPage: 1,
      externalPerPage: 5,
      externalCurrentPage: 1,
      perPageOptions: [
        { value: 5, text: "5" },
        { value: 10, text: "10" },
        { value: 15, text: "15" },
        { value: 20, text: "20" },
      ],
      // Table fields
      fieldsInternal: [
        { key: "year", label: "Year", class: "text-center" },
        { key: "make", label: "Make", class: "text-center" },
        { key: "model", label: "Model", class: "text-center" },
        { key: "trim", label: "Trim", class: "text-center" },
        {
          key: "mileage",
          label: "Mileage",
          class: "text-center",
          formatter: (value) => this.$options.filters.formatNumber(value),
        },
        { key: "color", label: "Color", class: "text-center" },
        {
          key: "date",
          label: "Date",
          class: "text-center",
        },
        { key: "price", label: "Price", class: "text-center" },
      ],
      fields: [
        { key: "name", label: "Provider", class: "text-center" },
        { key: "year", label: "Year", class: "text-center" },
        { key: "make", label: "Make", class: "text-center" },
        { key: "model", label: "Model", class: "text-center" },
        { key: "trim", label: "Trim", class: "text-center" },
        {
          key: "mileage",
          label: "Mileage",
          class: "text-center",
          formatter: (value) => this.$options.filters.formatNumber(value),
        },
        { key: "color", label: "Color", class: "text-center" },
        { key: "date", label: "Date  ", class: "text-center" },
        { key: "price", label: "Price", class: "text-center" },
      ],
      // VIN field definition
      vinField: { key: "vin", label: "VIN", class: "text-center" },
    };
  },
  methods: {
    // Toggle visibility of VIN column
    toggleVinColumn() {
      this.showVinColumn = !this.showVinColumn;
    },

    // Format currency values
    formatCurrency(value) {
      if (value === null || value === undefined) return "-";
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
      }).format(value);
    },

    // Format date values
    formatDate(date) {
      if (!date) return "-";
      return new Date(date).toLocaleDateString();
    },

    downloadExcel(type) {
      // Emit event to parent component to handle Excel download
      this.$emit("download-excel", type);
    },
  },
};
</script>

<style scoped>
.bg-light-grey {
  background-color: #f8f9fa;
}

.text-break {
  word-break: break-word;
}

/* Smaller font size for tables */
.table th,
.table td {
  font-size: 0.75rem; /* 75% of the default font size */
  padding: 0.35rem !important;
  line-height: 1.2;
}

/* Compact table specific styles */
.compact-table {
  margin-bottom: 0.5rem;
}

.compact-table th {
  font-weight: 600;
  white-space: nowrap;
}

/* Reduce h5 and h6 sizes */
h5 {
  font-size: 1rem;
}

h6 {
  font-size: 0.85rem;
}

/* Make pagination text smaller */
.pagination .page-link {
  font-size: 0.75rem;
  padding: 0.2rem 0.4rem;
  line-height: 1.2;
}

/* Compact controls for pagination and selectors */
.compact-controls {
  min-height: 30px;
}

.compact-controls .pagination {
  margin: 0;
}

/* Make select control text smaller */
.form-control {
  font-size: 0.75rem;
  height: calc(1.5em + 0.5rem + 2px);
  padding: 0.25rem 0.5rem;
}

/* Reduce spinner size */
.spinner-border {
  width: 1rem;
  height: 1rem;
}
</style>
